// 検索結果のキーワードハイライト（community_contents / anchors の検索ページで使用）。
// 旧 javascripts/utils/highlighter.js を TypeScript 化したもの。ロジックは変えていない。

export type HighlightOptions = {
  backgroundColor?: string;
  fontStyle?: string;
  color?: string;
};

/**
 * ハイライト対象のキーワード。ERB の data 属性由来で不揃いな値が入りうるため、
 * 文字列以外・空文字は無視する（配列以外が渡された場合は何もしない）。
 */
export type HighlightKeywords = ReadonlyArray<string | null | undefined> | null | undefined;

const DEFAULT_OPTIONS: Required<HighlightOptions> = {
  backgroundColor: 'rgb(255, 255, 0)',
  fontStyle: 'italic',
  color: 'inherit',
};

type SpecialChar = '&' | '<' | '>' | '"' | '\'';

// 特殊文字を素の形と entity 形両方にマッチする regex 片に展開（例: 'Q&A' → 'Q(?:&amp;|&)A'）
const HTML_ENTITY_OR_RAW: Record<SpecialChar, string> = {
  '&': '(?:&amp;|&)',
  '<': '(?:&lt;|<)',
  '>': '(?:&gt;|>)',
  '"': '(?:&quot;|")',
  '\'': '(?:&#39;|\')',
};

// `&NAME;` の保護解除判定に使う entity 名（`&` を除いた中身）
const ENTITY_NAME_FOR_CHAR: Record<SpecialChar, string> = {
  '&': 'amp',
  '<': 'lt',
  '>': 'gt',
  '"': 'quot',
  '\'': '#39',
};

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * HTML 文字列中のキーワードを <mark> で囲む
 * @param htmlContent - 検索対象の HTML 文字列
 * @param keywords - ハイライトするキーワード
 * @param options - <mark> のスタイル
 * @returns キーワードを <mark> で囲んだ HTML 文字列
 */
export function highlightKeywordsInHtml(
  htmlContent: string,
  keywords: HighlightKeywords,
  options: HighlightOptions = {},
): string {
  const { backgroundColor, fontStyle, color } = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  const keywordsArray = Array.isArray(keywords) ? keywords : [];
  const uniqueKeywords = Array.from(
    new Set(keywordsArray.filter((k): k is string => typeof k === 'string' && k.length > 0)),
  );
  if (uniqueKeywords.length === 0) return htmlContent;

  // 長さ降順: alternation は leftmost-alternative-first のため、
  // "A" と "AB" の両方がある時に "AB" を優先させる
  const sortedKeywords = uniqueKeywords.sort((a, b) => b.length - a.length);
  const pattern = sortedKeywords
    .map(escapeRegExp)
    .map((k) => k.replace(/[&<>"']/g, (c) => HTML_ENTITY_OR_RAW[c as SpecialChar]))
    .join('|');
  // 特殊文字を含むキーワードがある entity は、そのキーワード側にマッチさせるため保護対象から外す。
  // ただし他キーワードが entity 内部に誤マッチして `&am<mark>p</mark>;` のように壊す場合
  // （例: `['A&B', 'p']`）は除外しない。
  const excludedEntities = (Object.entries(ENTITY_NAME_FOR_CHAR) as [SpecialChar, string][])
    .filter(([char, name]) => {
      const hasChar = uniqueKeywords.some((k) => k.includes(char));
      if (!hasChar) return false;
      // 特殊文字単体がキーワードなら、展開パターンが entity 全体を先頭から消費するため誤マッチは起こらない
      if (uniqueKeywords.includes(char)) return true;
      // `&NAME;` の `NAME;` 部分にキーワードが部分文字列として現れる場合のみ競合とみなす。
      // 逆方向の `lowerK.includes(name)` は `'amplifier'` のような長いキーワードを誤検知するため不採用。
      const nameWithSemicolon = `${name};`;
      const hasConflict = uniqueKeywords.some((k) => {
        if (k.includes(char)) return false;
        // regex は `gi` フラグ。大文字小文字を区別しない比較が必要
        const lowerK = k.toLowerCase();
        return nameWithSemicolon.includes(lowerK);
      });
      return !hasConflict;
    })
    .map(([, name]) => name);
  const entityPattern =
    excludedEntities.length > 0
      ? `&(?!(?:${excludedEntities.join('|')});)[a-zA-Z0-9#]+;`
      : '&[a-zA-Z0-9#]+;';
  // 既存 HTML タグ (<...>) と HTML エンティティ (&amp; など) をキャプチャして保護しつつ、
  // キーワードにマッチさせる
  const regex = new RegExp(`(<[^>]+>|${entityPattern})|(${pattern})`, 'gi');

  // 1パス置換: 挿入後の <mark> 属性文字列に次キーワードが再マッチするバグを防ぐ
  return htmlContent.replace(
    regex,
    (match: string, tagMatch: string | undefined, keywordMatch: string | undefined) => {
      if (tagMatch) return tagMatch; // マッチしたのがタグまたはエンティティの場合はそのまま返す
      // 正規表現の構造上 tagMatch が無ければ keywordMatch が必ずあるが、型どおりに防御しておく
      if (!keywordMatch) return match;
      return `<mark style="background-color: ${backgroundColor}; font-style: ${fontStyle}; color: ${color};">${keywordMatch}</mark>`;
    },
  );
}

/**
 * container 内の各 element の innerHTML をハイライトする
 * @param containerSelector - container 要素のセレクタ（例: '.js-highlight-container'）
 * @param elementSelector - container 内でハイライトする要素のセレクタ（例: '.js-highlight'）
 * @param getKeywords - container と element からキーワードを取り出す関数
 * @param options - <mark> のスタイル
 */
export function highlightElementsInContainers(
  containerSelector: string,
  elementSelector: string,
  getKeywords: (container: HTMLElement, element: HTMLElement) => HighlightKeywords,
  options: HighlightOptions = {},
): void {
  const containers = document.querySelectorAll<HTMLElement>(containerSelector);

  containers.forEach((container) => {
    const elements = container.querySelectorAll<HTMLElement>(elementSelector);

    elements.forEach((element) => {
      const keywords = getKeywords(container, element);
      if (!Array.isArray(keywords) || keywords.length === 0) return;

      const originalHtml = element.innerHTML;
      const highlightedHtml = highlightKeywordsInHtml(originalHtml, keywords, options);

      if (highlightedHtml !== originalHtml) {
        element.innerHTML = highlightedHtml;
      }
    });
  });
}
